import {
    alpha,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SwitchRightIcon from '@mui/icons-material/SwitchRight';
import SwitchLeftIcon from '@mui/icons-material/SwitchLeft';
import GroupIcon from '@mui/icons-material/Group';
import PaymentIcon from '@mui/icons-material/Payment';
import ExtensionIcon from '@mui/icons-material/Extension';
import DomainIcon from '@mui/icons-material/Domain';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link, useNavigate } from 'react-router-dom';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useTranslation } from 'react-i18next';

const CARD_ICON_HEIGHT = 120;

const VenueCard = ({ row, onDelete, onSwitch, isSwitching, isDeleting, isCurrent }) => {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const canManageVenues = can(CAPABILITIES.VENUES_MANAGE);
    const canViewPayments = can(CAPABILITIES.PAYMENT_METHODS_VIEW);
    const canManageUsers = can(CAPABILITIES.USERS_MANAGE);
    const canManageIntegrations = can(CAPABILITIES.INTEGRATIONS_MANAGE);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCardClick = () => {
        navigate(`/venues/edit/${row.id}`);
    };

    const inlineActions = [
        ...(canViewPayments
            ? [
                  {
                      key: 'payments',
                      label: t('paymentmethods.title'),
                      icon: <PaymentIcon fontSize="small" />,
                      to: `/venues/${row.id}/paymentmethods`,
                  },
              ]
            : []),
        ...(canManageUsers
            ? [
                  {
                      key: 'users',
                      label: t('users.title'),
                      icon: <GroupIcon fontSize="small" />,
                      to: `/venues/${row.id}/users`,
                  },
              ]
            : []),
        ...(canManageIntegrations
            ? [
                  {
                      key: 'integrations',
                      label: t('integrations.title'),
                      icon: <ExtensionIcon fontSize="small" />,
                      to: `/venues/${row.id}/integrations`,
                  },
              ]
            : []),
        ...(canManageVenues
            ? [
                  {
                      key: 'edit',
                      label: t('common.edit'),
                      icon: <EditIcon fontSize="small" />,
                      to: `/venues/edit/${row.id}`,
                  },
                  {
                      key: 'switch',
                      label: isCurrent ? t('venues.isCurrent') : t('users.switchToVenue'),
                      icon: isCurrent ? (
                          <SwitchRightIcon fontSize="small" />
                      ) : (
                          <SwitchLeftIcon fontSize="small" />
                      ),
                      onClick: () => onSwitch(),
                      disabled: isCurrent || isSwitching,
                  },
              ]
            : []),
    ];

    const deleteAction = canManageVenues
        ? {
              label: t('common.delete'),
              icon: <DeleteIcon fontSize="small" color="error" />,
              onClick: () => {
                  handleMenuClose();
                  onDelete();
              },
              disabled: isDeleting,
          }
        : null;

    const cardBody = (
        <>
            <Box
                sx={(theme) => ({
                    height: CARD_ICON_HEIGHT,
                    bgcolor: isCurrent
                        ? alpha(theme.palette.success.main, 0.12)
                        : theme.palette.grey[100],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                })}
            >
                <DomainIcon
                    sx={{ fontSize: 56, color: isCurrent ? 'success.light' : 'grey.400' }}
                />
                {isCurrent && (
                    <Chip
                        label={t('venues.isCurrent')}
                        color="success"
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                    />
                )}
            </Box>
            <CardContent sx={{ pb: 1 }}>
                <Typography variant="h6" component="h2" noWrap title={row.name}>
                    {row.name}
                </Typography>
                {/* Always render the site line (blank placeholder when empty) so every
                    card has the same content height and the icon header stays uniform. */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={row.site || undefined}
                >
                    {row.site || ' '}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    {t('venues.columns.allowedSplitPaymentAmount')}: {row.allowedSplitPaymentAmount}
                </Typography>
            </CardContent>
        </>
    );

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s ease-in-out',
                '&:hover': { boxShadow: 4 },
                border: '1px solid',
                borderColor: isCurrent ? 'success.light' : 'grey.300',
            }}
        >
            {canManageVenues ? (
                <CardActionArea onClick={handleCardClick} sx={{ flexGrow: 1 }}>
                    {cardBody}
                </CardActionArea>
            ) : (
                <Box sx={{ flexGrow: 1 }}>{cardBody}</Box>
            )}

            <Divider />
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', rowGap: 0.25 }}
                >
                    {inlineActions.map((action) =>
                        action.to ? (
                            <Tooltip key={action.key} title={action.label}>
                                <IconButton size="small" component={Link} to={action.to}>
                                    {action.icon}
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip key={action.key} title={action.label}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={action.onClick}
                                        disabled={action.disabled}
                                    >
                                        {action.icon}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ),
                    )}
                </Stack>
                {deleteAction && (
                    <Box sx={{ ml: 'auto' }}>
                        <Tooltip title={t('common.more')}>
                            <IconButton size="small" onClick={handleMenuOpen}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {deleteAction && (
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { sx: { mt: 0.5 } } }}
                >
                    <MenuItem onClick={deleteAction.onClick} disabled={deleteAction.disabled}>
                        <ListItemIcon>{deleteAction.icon}</ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>
                            {deleteAction.label}
                        </ListItemText>
                    </MenuItem>
                </Menu>
            )}
        </Card>
    );
};

export const VenueCardSkeleton = () => (
    <Card sx={{ height: '100%' }}>
        <Skeleton variant="rectangular" height={CARD_ICON_HEIGHT} />
        <CardContent>
            <Skeleton variant="text" width="70%" height={32} />
            <Skeleton variant="text" width="50%" height={20} />
        </CardContent>
        <Divider />
        <Box sx={{ p: 1 }}>
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                </Stack>
                <Skeleton variant="circular" width={32} height={32} />
            </Stack>
        </Box>
    </Card>
);

export default VenueCard;
